import { type FC, type KeyboardEvent, type Ref, useState } from "react";
import { Input } from "../../form/inputs/Input";
import {
	ActionMenu,
	ClickableIcon,
	type ClickableIconProps,
	Icon,
	type IconType,
	Text,
} from "../../primitives";
import { trackingAttrs } from "../../utils";
import { useViewerIds } from "./viewerIds";
import type { ZoomMode } from "./viewerSession";
import { FIT_MODES, nextZoomStep, ZOOM_PRESETS, zoomPercent } from "./zoom";

/** `compact` is a viewer under 560 px wide, with everything that doesn't fit moved into More. */
export type ViewerToolbarVariant = "embedded" | "compact";

/** Shown in place of the page count and zoom level until the document is on screen. */
const NOT_LOADED = "–";

type PageInputProps = {
	page: number;
	pageCount: number;
	ready: boolean;
	onPageChange: (page: number) => void;
};

/** Digits only; Enter jumps, Escape or leaving the field puts the current page back. */
const PageInput: FC<PageInputProps> = ({ page, pageCount, ready, onPageChange }) => {
	const ids = useViewerIds();
	const [draft, setDraft] = useState<string | undefined>(undefined);
	const commit = () => {
		const target = Number(draft);
		if (draft !== undefined && draft !== "" && Number.isFinite(target)) onPageChange(target);
		setDraft(undefined);
	};
	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") commit();
		else if (event.key === "Escape" && draft !== undefined) {
			event.stopPropagation();
			setDraft(undefined);
		}
	};
	return (
		<div className="flex items-center gap-2">
			<Input
				compact
				containerClassName="w-11"
				className="text-center tabular-nums"
				inputMode="numeric"
				aria-label="Page number"
				placeholder={NOT_LOADED}
				disabled={!ready}
				value={draft ?? (ready ? String(page) : "")}
				onChange={(event) => setDraft(event.target.value.replace(/\D/g, ""))}
				onKeyDown={handleKeyDown}
				onBlur={() => setDraft(undefined)}
				testId={ids.testId("page-input")}
				{...trackingAttrs(ids.trackingId("page-input"))}
				{...ids.fileTracking}
			/>
			<Text
				type="caption"
				color="secondary"
				className="whitespace-nowrap tabular-nums"
				data-testid={ids.testId("page-count")}
			>
				of {ready ? pageCount : NOT_LOADED}
			</Text>
		</div>
	);
};

type ViewerToolbarProps = {
	variant: ViewerToolbarVariant;
	ready: boolean;
	page: number;
	pageCount: number;
	scale: number;
	zoom: ZoomMode;
	findOpen: boolean;
	/** False when there is no file to download. */
	canDownload: boolean;
	downloading: boolean;
	printing: boolean;
	onPageChange: (page: number) => void;
	onZoomChange: (zoom: ZoomMode) => void;
	onZoomStep: (direction: 1 | -1) => void;
	onToggleFind: () => void;
	onRotate: (direction: 1 | -1) => void;
	onDownload: () => void;
	onPrint: () => void;
	findButtonRef: Ref<HTMLButtonElement>;
};

/**
 * Page navigation, zoom, find, rotate, download and print, laid out for its container. It
 * wraps when narrow. Every control waits for the document except Download, which only needs a
 * file.
 */
export const ViewerToolbar: FC<ViewerToolbarProps> = ({
	variant,
	ready,
	page,
	pageCount,
	scale,
	zoom,
	findOpen,
	canDownload,
	downloading,
	printing,
	onPageChange,
	onZoomChange,
	onZoomStep,
	onToggleFind,
	onRotate,
	onDownload,
	onPrint,
	findButtonRef,
}) => {
	const ids = useViewerIds();
	const compact = variant === "compact";
	const zoomLevel = ready ? zoomPercent(scale) : NOT_LOADED;
	const zoomLabel = FIT_MODES.find(({ mode }) => mode === zoom)?.label ?? zoomLevel;

	const toolbarIcon = (
		type: IconType,
		label: string,
		suffix: string,
		onClick: () => void,
		disabled: boolean,
		extra?: Partial<ClickableIconProps>,
	) => (
		<ClickableIcon
			type={type}
			size="small"
			color="secondary"
			aria-label={label}
			tooltip={label}
			disabled={disabled}
			onClick={onClick}
			{...ids.control(suffix)}
			{...extra}
		/>
	);

	/** `current` marks the zoom option in use. */
	const menuItem = (
		label: string,
		suffix: string,
		onClick: () => void,
		disabled: boolean,
		current?: boolean,
	) => (
		<ActionMenu.Item
			key={suffix}
			aria-current={current || undefined}
			disabled={disabled}
			onClick={onClick}
			data-testid={ids.testId(suffix)}
			trackingId={ids.trackingId(suffix)}
			{...ids.fileTracking}
		>
			{label}
		</ActionMenu.Item>
	);

	const findToggle = toolbarIcon(
		"magnifying-glass",
		"Find in document",
		"find-toggle",
		onToggleFind,
		!ready,
		{ ref: findButtonRef, "aria-pressed": findOpen },
	);

	const zoomLevelText = (
		<Text
			type="caption"
			color="secondary"
			className="min-w-[30px] text-center tabular-nums"
			data-testid={ids.testId("zoom-level")}
		>
			{zoomLevel}
		</Text>
	);

	const busy = (label: string, working: boolean) => (working ? `${label}…` : label);

	return (
		<div
			role="toolbar"
			aria-label="Document controls"
			className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 border-b border-bc-default bg-surface-base px-3 py-1.5"
			data-testid={ids.testId("toolbar")}
			data-variant={variant}
		>
			<div className="flex items-center gap-2">
				{toolbarIcon(
					"chevron-left",
					"Previous page",
					"previous-page",
					() => onPageChange(page - 1),
					!ready || page <= 1,
				)}
				{compact ? (
					<Text
						type="caption"
						color="secondary"
						className="whitespace-nowrap tabular-nums"
						aria-label={ready ? `Page ${page} of ${pageCount}` : undefined}
						data-testid={ids.testId("page-count")}
					>
						{ready ? `${page} / ${pageCount}` : `${NOT_LOADED} / ${NOT_LOADED}`}
					</Text>
				) : (
					<PageInput page={page} pageCount={pageCount} ready={ready} onPageChange={onPageChange} />
				)}
				{toolbarIcon(
					"chevron-right",
					"Next page",
					"next-page",
					() => onPageChange(page + 1),
					!ready || page >= pageCount,
				)}
			</div>
			<div className="flex items-center gap-1">
				{toolbarIcon(
					"minus-circle",
					"Zoom out",
					"zoom-out",
					() => onZoomStep(-1),
					!ready || nextZoomStep(scale, -1) === undefined,
				)}
				{compact ? (
					zoomLevelText
				) : (
					<ActionMenu
						anchor="bottom start"
						trigger={
							<span className="flex items-center gap-1 px-1 py-0.5">
								{zoomLevelText}
								<Icon type="chevron-down" size="micro" color="tertiary" />
							</span>
						}
						triggerAriaLabel={`Zoom: ${zoomLabel}`}
						menuClassName="min-w-[140px]"
						testId={ids.testId("zoom-menu")}
						trackingId={ids.trackingId("zoom-menu")}
						triggerDataAttributes={ids.fileTracking}
					>
						{FIT_MODES.map(({ mode, label }) =>
							menuItem(label, `zoom-${mode}`, () => onZoomChange(mode), !ready, zoom === mode),
						)}
						<ActionMenu.Divider />
						{ZOOM_PRESETS.map((preset) =>
							menuItem(
								zoomPercent(preset),
								`zoom-${preset * 100}`,
								() => onZoomChange(preset),
								!ready,
								zoom === preset,
							),
						)}
					</ActionMenu>
				)}
				{toolbarIcon(
					"plus-circle",
					"Zoom in",
					"zoom-in",
					() => onZoomStep(1),
					!ready || nextZoomStep(scale, 1) === undefined,
				)}
			</div>
			<div className="ml-auto flex items-center gap-2">
				{!compact && findToggle}
				{toolbarIcon(
					"arrow-down-tray",
					busy("Download", downloading),
					"download",
					onDownload,
					!canDownload || downloading,
				)}
				<ActionMenu
					trigger={
						<span className="flex p-0.5">
							<Icon type="ellipsis-horizontal" size="small" color="secondary" />
						</span>
					}
					triggerAriaLabel="More document actions"
					testId={ids.testId("more")}
					trackingId={ids.trackingId("more")}
					triggerDataAttributes={ids.fileTracking}
				>
					{compact && menuItem("Find in document", "find", onToggleFind, !ready)}
					{menuItem("Rotate clockwise", "rotate-clockwise", () => onRotate(1), !ready)}
					{menuItem(
						"Rotate counter-clockwise",
						"rotate-counter-clockwise",
						() => onRotate(-1),
						!ready,
					)}
					{menuItem(busy("Print", printing), "print", onPrint, !ready || printing)}
				</ActionMenu>
			</div>
		</div>
	);
};
